import { uuid } from "@hex/common";
import { Draft, castDraft } from "immer";
import { identity, isMatch, partialRight } from "lodash";
import { DistributedOmit } from "type-fest";

import {
  WorkbenchTab,
  WorkbenchTabGroup,
  WorkbenchTabGroupId,
  WorkbenchTabId,
  WorkbenchViewport,
  closeTab,
  filterTabs,
  focusTab,
  newTab,
  updateTab,
  updateViewportToMatchTabGroups,
} from "../../../components/workbench/state";

type PartialWorkbenchState<TTab extends WorkbenchTab> = {
  tabGroups: WorkbenchTabGroup<TTab>[];
  viewport: WorkbenchViewport;
};

export type NewWorkbenchTabPayload<TTab extends WorkbenchTab> = {
  tab: DistributedOmit<TTab, "id">;
  tabGroupId: WorkbenchTabGroupId | null;
};

/**
 * Create a new tab in the editor view within the specified tab group, or a
 * new tab group if none is specified.
 */
export function newWorkbenchTab<
  TState extends PartialWorkbenchState<TTab>,
  TTab extends WorkbenchTab,
>(state: Draft<TState>, payload: NewWorkbenchTabPayload<TTab>): void {
  state.tabGroups = newTab(
    state.tabGroups,
    castDraft({
      id: WorkbenchTabId.check(uuid()),
      ...payload.tab,
      // cast to avoid constraint subtype error; this is an acceptable tab shape
    } as TTab),
    payload.tabGroupId,
  );
  state.viewport = updateViewportToMatchTabGroups(
    state.tabGroups,
    state.viewport,
  );
}

export type FocusWorkbenchTabPayload = {
  tabId: WorkbenchTabId;
  promotePreview?: boolean;
};

/**
 * Focus a tab in the editor view, optionally promoting it from a preview
 * tab. If the tab does not exist, nothing happens.
 */
export function focusWorkbenchTab<
  TState extends PartialWorkbenchState<TTab>,
  TTab extends WorkbenchTab,
>(state: Draft<TState>, payload: FocusWorkbenchTabPayload): void {
  state.tabGroups = focusTab(
    state.tabGroups,
    payload.tabId,
    payload.promotePreview,
  );
  state.viewport = updateViewportToMatchTabGroups(
    state.tabGroups,
    state.viewport,
  );
}

export type CloseWorkbenchTabPayload = {
  tabId: WorkbenchTabId;
};

/**
 * Close the specified tab in the editor view. If the tab does not exist,
 * does nothing.
 */
export function closeWorkbenchTab<
  TState extends PartialWorkbenchState<TTab>,
  TTab extends WorkbenchTab,
>(state: Draft<TState>, payload: CloseWorkbenchTabPayload): void {
  state.tabGroups = closeTab(state.tabGroups, payload.tabId);
  state.viewport = updateViewportToMatchTabGroups(
    state.tabGroups,
    state.viewport,
  );
}

export type UpdateWorkbenchTabPayload<TTab extends WorkbenchTab> = {
  tabId: WorkbenchTabId;
  producerFn: (tab: Draft<TTab>) => void | Draft<TTab>;
};

/**
 * Update the specified tab in the editor view. If the tab does not exist,
 * does nothing.
 */
export function updateWorkbenchTab<
  TState extends PartialWorkbenchState<TTab>,
  TTab extends WorkbenchTab,
>(state: Draft<TState>, payload: UpdateWorkbenchTabPayload<TTab>): void {
  state.tabGroups = updateTab<Draft<TTab>>(
    state.tabGroups,
    payload.tabId,
    // @ts-expect-error duck this error
    payload.producerFn,
  );
}

export type OpenInWorkbenchTabPayload<TTab extends WorkbenchTab> = Omit<
  TTab,
  keyof WorkbenchTab
>;

export function openInWorkbenchTab<
  TState extends PartialWorkbenchState<TTab>,
  TTab extends WorkbenchTab,
>(
  state: Draft<TState>,
  payload: OpenInWorkbenchTabPayload<TTab>,
  options?: {
    /** @default - matches all values in the payload */
    matchExistingTab?: (tab: Draft<TTab>) => boolean;
    /** @default - spreads payload as the properties */
    getNewTabProperties?: (
      payload: OpenInWorkbenchTabPayload<TTab>,
    ) => Omit<TTab, keyof WorkbenchTab>;
  },
): void {
  const {
    getNewTabProperties = identity,
    matchExistingTab = partialRight(isMatch, payload),
  } = options ?? {};

  const existingTab = filterTabs(state.tabGroups, matchExistingTab)[0];

  if (existingTab) {
    state.tabGroups = focusTab(state.tabGroups, existingTab.id);
  } else {
    const primaryGroupId = state.tabGroups[0]?.id ?? null;
    state.tabGroups = newTab(
      state.tabGroups,
      castDraft({
        id: WorkbenchTabId.check(uuid()),
        isPreview: true,
        ...getNewTabProperties(payload),
      } as TTab),
      primaryGroupId,
    );
  }
  state.viewport = updateViewportToMatchTabGroups(
    state.tabGroups,
    state.viewport,
  );
}
